import axiosClient from './axiosClient';

// --- Newsletter API Calls ---

/**
 * `source` is optional so existing callers keep working, but every new surface
 * should pass one — the server stores it on the subscriber, which is the only
 * way to tell later whether the blog or the footer is actually earning
 * addresses.
 */
export const subscribeToNewsletterApi = async (email: string, source?: string): Promise<void> => {
  await axiosClient.post('/newsletter/subscribe', source ? { email, source } : { email });
};

export const unsubscribeFromNewsletterApi = async (token: string): Promise<{ status: string }> => {
  const response = await axiosClient.post('/newsletter/unsubscribe', { token });
  return response.data;
};

export interface NewsletterSubscriber {
  _id: string;
  email: string;
  status: 'subscribed' | 'unsubscribed';
  source: string;
  subscribedAt: string;
}

export const listSubscribers = async (
  status: 'subscribed' | 'unsubscribed' = 'subscribed',
): Promise<{ total: number; subscribers: NewsletterSubscriber[] }> => {
  const response = await axiosClient.get('/newsletter/subscribers', { params: { status } });
  return response.data;
};

/**
 * Audience segments.
 *
 * A campaign stores the *question* — "everyone who stalled halfway through a
 * course for a fortnight" — not the list it produced when it was written. The
 * server re-resolves it at send time, so the count shown in the composer is a
 * preview rather than a promise.
 */
export interface SegmentParamDef {
  name: string;
  label: string;
  type: 'course' | 'number' | 'select';
  required?: boolean;
  default?: string | number;
  options?: { value: string; label: string }[];
  help?: string;
}

export interface SegmentDef {
  key: string;
  label: string;
  description: string;
  group: string;
  params: SegmentParamDef[];
}

export interface SegmentCatalogue {
  groups: string[];
  roleDescriptions: Record<string, string>;
  segments: SegmentDef[];
}

export interface SegmentRef {
  key: string;
  params?: Record<string, string | number>;
}

export interface AudienceSpec {
  include: SegmentRef[];
  match: 'any' | 'all';
  exclude?: SegmentRef[];
}

/**
 * What a campaign means when nothing has been chosen — and what every campaign
 * written before segments existed meant. Kept beside the types rather than in
 * the builder so the composer and the server agree on the fallback.
 */
export const DEFAULT_AUDIENCE: AudienceSpec = {
  include: [{ key: 'newsletter_subscribers' }],
  match: 'any',
};

export interface AudiencePreview {
  total: number;
  label: string;
  sample: { email: string; name: string; source: 'subscriber' | 'user' }[];
}

export const listSegments = async (): Promise<SegmentCatalogue> => {
  const response = await axiosClient.get('/newsletter/segments');
  return response.data;
};

export const previewAudience = async (audience: AudienceSpec): Promise<AudiencePreview> => {
  const response = await axiosClient.post('/newsletter/audience/preview', { audience });
  return response.data;
};

export interface NewsletterCampaign {
  _id: string;
  subject: string;
  body?: string;
  preheader?: string;
  audience?: AudienceSpec;
  /** One-line summary of the audience, written by the server. */
  audienceLabel?: string;
  status: 'draft' | 'sending' | 'sent' | 'failed';
  createdByName: string;
  recipientCount: number;
  sentCount: number;
  failedCount: number;
  sentAt?: string;
  lastError?: string;
  createdAt: string;
  updatedAt: string;
}

export const listCampaigns = async (): Promise<{ campaigns: NewsletterCampaign[] }> => {
  const response = await axiosClient.get('/newsletter/campaigns');
  return response.data;
};

export const getCampaign = async (id: string): Promise<NewsletterCampaign> => {
  const response = await axiosClient.get(`/newsletter/campaigns/${id}`);
  return response.data;
};

export const createCampaign = async (payload: {
  subject: string;
  body: string;
  preheader?: string;
  audience?: AudienceSpec;
}): Promise<NewsletterCampaign> => {
  const response = await axiosClient.post('/newsletter/campaigns', payload);
  return response.data;
};

export const updateCampaign = async (
  id: string,
  payload: { subject?: string; body?: string; preheader?: string; audience?: AudienceSpec },
): Promise<NewsletterCampaign> => {
  const response = await axiosClient.put(`/newsletter/campaigns/${id}`, payload);
  return response.data;
};

export const deleteCampaign = async (id: string): Promise<void> => {
  await axiosClient.delete(`/newsletter/campaigns/${id}`);
};

export const sendTestCampaign = async (id: string, email: string): Promise<{ sent: boolean }> => {
  const response = await axiosClient.post(`/newsletter/campaigns/${id}/test`, { email });
  return response.data;
};

/**
 * Returns 202 as soon as the run starts, not when it finishes — the send
 * outlives the request. The admin screen polls `getCampaign` for progress.
 */
export const sendCampaign = async (
  id: string,
): Promise<{ status: string; recipientCount: number }> => {
  const response = await axiosClient.post(`/newsletter/campaigns/${id}/send`);
  return response.data;
};
